import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthenticatedLayout } from '../components/layout';
import { ProtectedRoute } from '../components/auth';
import { RoleProtectedRoute } from '../hooks/useRoleAccess';
import { HomePage, NotFoundPage, AboutUsPage, MeetTheOfficersPage, NewsPageWarm, NewsPageHeritage, ContactUsPageHeritage, LoginPage, RegisterPage, MemberHomePage, AnnouncementsPage, MembersDirectoryPage, RegistrationSuccessPage } from '../pages';
import { AdminPortal } from '../components/layout';

// Lazy load larger page components to reduce initial bundle size
const DesignGalleryPage = lazy(() => import('../pages/DesignGalleryPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const AnnouncementsAdminPage = lazy(() => import('../pages/AnnouncementsAdminPage'));
const ReunionEventPage = lazy(() => import('../pages/ReunionEventPage'));
const PrintableItineraryPage = lazy(() => import('../pages/PrintableItineraryPage'));
const SurveyPage = lazy(() => import('../pages/SurveyPage'));
const ReunionEventEditPage = lazy(() => import('../pages/ReunionEventEditPage'));
const DuesPage = lazy(() => import('../pages/DuesPage'));
// const DirectoryPage = lazy(() => import('../pages/DirectoryPage'));
const MemberProfilePage = lazy(() => import('../pages/MemberProfilePage'));
const AdminMembersPage = lazy(() => import('../pages/AdminMembersPage'));
const UserManagementPage = lazy(() => import('../pages/UserManagementPage'));
const AdminPendingUsersPage = lazy(() => import('../pages/AdminPendingUsersPage'));
// Communication Admin Pages
const CommunicationsDashboardPage = lazy(() => import('../pages/CommunicationsDashboardPage'));
const EmailComposerPage = lazy(() => import('../pages/EmailComposerPage'));
const EmailTemplatesPage = lazy(() => import('../pages/EmailTemplatesPage'));
const CommunicationMonitoringPage = lazy(() => import('../pages/CommunicationMonitoringPage'));
const FamilyTreePage = lazy(() => import('../pages/FamilyTreePage').then(m => ({ default: m.FamilyTreePage })));
// Analytics Pages
const AnalyticsDashboardPage = lazy(() => import('../pages/admin/AnalyticsDashboardPage'));
// Admin Dashboard - removed, using sidebar navigation instead
// Payment System (PaymentPortalPage retired - redirects to /dues)
// Admin Payment Pages
const AdminPaymentDashboardPage = lazy(() => import('../pages/admin/AdminPaymentDashboardPage'));
const AdminCauseManagementPage = lazy(() => import('../pages/admin/AdminCauseManagementPage'));
const AdminPaymentReportsPage = lazy(() => import('../pages/admin/AdminPaymentReportsPage'));
const AdminExpensesPage = lazy(() => import('../pages/admin/AdminExpensesPage'));
const AdminDuesPage = lazy(() => import('../pages/admin/AdminDuesPage'));
const AdminAuditLogPage = lazy(() => import('../pages/admin/AdminAuditLogPage'));
const AdminTreeAdminsPage = lazy(() => import('../pages/admin/AdminTreeAdminsPage').then(m => ({ default: m.AdminTreeAdminsPage })));
// Document Library
const DocumentsPage = lazy(() => import('../pages/DocumentsPage'));
const AdminDocumentsPage = lazy(() => import('../pages/admin/AdminDocumentsPage'));
// Photo Gallery
const GalleryPage = lazy(() => import('../pages/GalleryPage'));
const AlbumPage = lazy(() => import('../pages/AlbumPage'));
// Payment Checkout
const PaymentCheckoutPage = lazy(() => import('../pages/PaymentCheckoutPage'));
// Game
const ReunionQuestPage = lazy(() => import('../components/game/ReunionQuestPage'));
const StandaloneQuestPage = lazy(() => import('../components/game/StandaloneQuestPage'));
// Donations
const DonationsPage = lazy(() => import('../pages/DonationsPage'));
// Ticketing
const PublicTicketsPage = lazy(() => import('../pages/PublicTicketsPage'));
const TicketConfirmationPage = lazy(() => import('../pages/TicketConfirmationPage'));
const TicketLookupPage = lazy(() => import('../pages/TicketLookupPage'));
const TicketLookupVerifyPage = lazy(() => import('../pages/TicketLookupVerifyPage'));
const MyTicketsPage = lazy(() => import('../pages/MyTicketsPage'));
const AdminTicketingPage = lazy(() => import('../pages/AdminTicketingPage'));
const AdminCheckInPage = lazy(() => import('../pages/AdminCheckInPage'));
const AdminTicketManifestPage = lazy(() => import('../pages/AdminTicketManifestPage'));
const AdminEventsPage = lazy(() => import('../pages/admin/AdminEventsPage'));
// Lazy load design mockup pages (they are large and rarely accessed)
// const Option1Heritage = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.Option1Heritage })));
// const Option2ModernHub = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.Option2ModernHub })));
// const Option3Interactive = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.Option3Interactive })));
// const Option4ModernHeritage = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.Option4ModernHeritage })));
// const Option5WarmHeritage = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.Option5WarmHeritage })));
// const PaymentPortal = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.PaymentPortal })));
// const AnnouncementCenter = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.AnnouncementCenter })));
// const RSVPFlow = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.RSVPFlow })));
// const MemberDashboard = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.MemberDashboard })));
// const FamilyDirectory = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.FamilyDirectory })));
// const DesignShowcase = lazy(() => import('../pages/design-mockups').then(m => ({ default: m.DesignShowcase })));
import { useAuthRedirect } from '../hooks/useAuthRedirect';

const ReunionRedirect: React.FC = () => {
  useEffect(() => {
    window.location.replace('/reunion.html');
  }, []);
  return null;
};

const RouterContent: React.FC = () => {
  const location = useLocation();
  
  // This hook automatically handles redirects when authentication state changes
  useAuthRedirect();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <>
    <Suspense fallback={
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-text-secondary text-lg font-medium">Loading...</p>
        </div>
      </div>
    }>
    <Routes>
      <Route
        path="/"
        element={<HomePage />}
      />

      {/* Authentication Routes */}
      <Route
        path="/login"
        element={<LoginPage />}
      />
      
      <Route
        path="/register"
        element={<RegisterPage />}
      />
      
      <Route
        path="/registration-success"
        element={<RegistrationSuccessPage />}
      />
      
      {/* Authenticated Home Page */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Home">
              <MemberHomePage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Redirect legacy /dashboard to /home */}
      <Route
        path="/dashboard"
        element={<Navigate to="/home" replace />}
      />

      {/* Protected Announcements Route */}
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Announcements">
              <AnnouncementsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Public Authentication Pages */}
      <Route
        path="/forgot-password"
        element={<ForgotPasswordPage />}
      />
      
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />

      {/* Design Gallery */}
      <Route
        path="/design-gallery"
        element={<DesignGalleryPage />}
      />

      {/* Protected Pages */}
      <Route
        path="/payments"
        element={<Navigate to="/dues" replace />}
      />
      
      <Route
        path="/payments/checkout"
        element={
          <ProtectedRoute>
            <PaymentCheckoutPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dues"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Dues & Payments">
              <DuesPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/directory"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Directory">
              <MembersDirectoryPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/members/:memberId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Member Profile">
              <MemberProfilePage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="My Profile">
              <MemberProfilePage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Printable Itinerary - intentionally without AuthenticatedLayout so app
          chrome doesn't appear in the printed output */}
      <Route
        path="/events/:eventSlug/print"
        element={
          <ProtectedRoute>
            <PrintableItineraryPage />
          </ProtectedRoute>
        }
      />

      {/* Reunion Event - Authenticated Only */}
      <Route
        path="/events/:eventSlug"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Reunion Event">
              <ReunionEventPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Reunion Survey - Authenticated Only */}
      <Route
        path="/survey"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Survey">
              <SurveyPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Document Library - All authenticated users */}
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Documents">
              <DocumentsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents/:folderId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Documents">
              <DocumentsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents/:folderId/:documentId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Documents">
              <DocumentsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Photo Gallery - All authenticated users */}
      <Route
        path="/gallery"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Gallery">
              <GalleryPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gallery/albums/:albumId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Gallery">
              <AlbumPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Reunion Quest Game */}
      <Route
        path="/quest"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Reunion Quest">
              <ReunionQuestPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quest/play"
        element={
          <ProtectedRoute>
            <StandaloneQuestPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Portal - All admin routes nested under this */}
      <Route
        path="/admin"
        element={
          <RoleProtectedRoute requiredPermissions={['canViewAdmin']}>
            <AdminPortal />
          </RoleProtectedRoute>
        }
      >
        <Route index element={<div className="p-8 text-center"><h2 className="text-2xl font-bold text-amber-900 mb-4">Admin Panel</h2><p className="text-amber-700">Use the sidebar navigation to access admin features.</p></div>} />
        <Route path="pending-users" element={<AdminPendingUsersPage />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="members" element={<AdminMembersPage />} />
        <Route path="announcements" element={<AnnouncementsAdminPage />} />
        <Route path="events" element={<AdminEventsPage />} />
        <Route path="events/:eventSlug/edit" element={<ReunionEventEditPage />} />
        <Route path="communications" element={<CommunicationsDashboardPage />} />
        <Route path="communications/compose" element={<EmailComposerPage />} />
        <Route path="communications/templates" element={<EmailTemplatesPage />} />
        <Route path="communications/monitoring" element={<CommunicationMonitoringPage />} />
        <Route path="communications/analytics" element={<AnalyticsDashboardPage />} />
        <Route path="payments" element={<AdminPaymentDashboardPage />} />
        <Route path="payments/donations" element={<AdminCauseManagementPage />} />
        <Route path="payments/reports" element={<AdminPaymentReportsPage />} />
        <Route path="expenses" element={<AdminExpensesPage />} />
        <Route path="dues" element={<AdminDuesPage />} />
        <Route path="audit-log" element={<AdminAuditLogPage />} />
        <Route path="documents" element={<AdminDocumentsPage />} />
        <Route path="tree-admins" element={<AdminTreeAdminsPage />} />
        <Route path="ticketing" element={<AdminTicketingPage />} />
        <Route path="ticketing/check-in" element={<AdminCheckInPage />} />
        <Route path="ticketing/items/:itemId/manifest" element={<AdminTicketManifestPage />} />
      </Route>
      
      {/* Simplified Communication Routes for Dashboard Navigation */}
      <Route
        path="/communications"
        element={
          <RoleProtectedRoute requiredPermissions={['canViewAllCommunications']}>
            <AuthenticatedLayout currentPage="Communications">
              <CommunicationsDashboardPage />
            </AuthenticatedLayout>
          </RoleProtectedRoute>
        }
      />
      
      <Route
        path="/email-composer"
        element={
          <RoleProtectedRoute requiredPermissions={['canSendCommunications']}>
            <AuthenticatedLayout currentPage="Email Composer">
              <EmailComposerPage />
            </AuthenticatedLayout>
          </RoleProtectedRoute>
        }
      />
      
      <Route
        path="/email-templates"
        element={
          <RoleProtectedRoute requiredPermissions={['canViewTemplates']}>
            <AuthenticatedLayout currentPage="Email Templates">
              <EmailTemplatesPage />
            </AuthenticatedLayout>
          </RoleProtectedRoute>
        }
      />
      
      {/* Public Reunion Info Page - redirects to standalone HTML */}
      <Route
        path="/reunion"
        element={<ReunionRedirect />}
      />

      {/* Public Donations */}
      <Route
        path="/donations"
        element={<DonationsPage />}
      />

      {/* Public Ticketing Routes */}
      <Route
        path="/tickets"
        element={<PublicTicketsPage />}
      />
      <Route
        path="/tickets/confirmation"
        element={<TicketConfirmationPage />}
      />
      <Route
        path="/tickets/lookup"
        element={<TicketLookupPage />}
      />
      <Route
        path="/tickets/verify"
        element={<TicketLookupVerifyPage />}
      />

      {/* Authenticated Ticketing */}
      <Route
        path="/my-tickets"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="My Tickets">
              <MyTicketsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/news"
        element={<NewsPageWarm />}
      />
      
      <Route
        path="/news-heritage"
        element={<NewsPageHeritage />}
      />
      
      <Route
        path="/contact"
        element={<ContactUsPageHeritage />}
      />
      
      <Route
        path="/about"
        element={<AboutUsPage />}
      />

      <Route
        path="/officers"
        element={<MeetTheOfficersPage />}
      />
      
      <Route
        path="/family"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout currentPage="Family Tree">
              <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>}>
                <FamilyTreePage />
              </Suspense>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Design Mockups Routes - Archived */}
            <Route
        path="/404"
        element={<NotFoundPage onGoHome={handleGoHome} />}
      />
      
      <Route
        path="*"
        element={<Navigate to="/404" replace />}
      />
    </Routes>
    </Suspense>
    </>
  );
};

const RouterProvider: React.FC = () => {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};

export { RouterProvider };